import React, { useEffect, useMemo, useState } from 'react';
import { Box, Spinner, Text } from '@chakra-ui/react';
import axios from 'axios';
import dayjs from 'dayjs';
import { baseUrl } from '../../../../hooks/useEndpoints';
import { ISession } from '../../../../models/sessions';
import { IPeriod } from '../../hooks/useCarichiTable';
import {
  leoTokens, roleColors, getInitials, primaryRole, installLeoStyles,
} from '../../theme/leoTokens';
import CustomSelectInput from '../../../../shared/CustomSelectInput/CustomSelectInput';

/* ──────────────────────────────────────────────────────────────────────────
 * ReportAthleteSubview — Carichi Esterni
 * Replica report standard cartaceo:
 *   - Header: athlete selector con avatar + badge ruolo + periodo
 *   - KPI cards con accent-top colorato
 *   - Tabella sessioni + riga Σ Totali con border-top accent
 * Drop-in del file originale. Props invariati (aggiunto opt. roles su roster).
 * ────────────────────────────────────────────────────────────────────────── */

interface ReportAthleteSubviewProps {
  rosterAthletes: {
    _id: string; firstName: string; lastName: string; roles?: string[];
  }[];
  period: IPeriod;
}

type KpiAccent = 'success' | 'primary' | 'warning' | 'error' | 'neutral';
const KPI_ACCENT: Record<KpiAccent, { bar: string; fg: string }> = {
  success: { bar: leoTokens.success, fg: leoTokens.successInk },
  primary: { bar: leoTokens.brandStrong, fg: '#7a5d10' },
  warning: { bar: leoTokens.warning, fg: leoTokens.warningInk },
  error: { bar: leoTokens.error, fg: leoTokens.errorInk },
  neutral: { bar: leoTokens.secondary0, fg: leoTokens.ink },
};

export const ReportAthleteSubview: React.FC<ReportAthleteSubviewProps> = ({
  rosterAthletes, period,
}) => {
  useEffect(() => { installLeoStyles(); }, []);
  const [selectedAthleteId, setSelectedAthleteId] = useState<string>(rosterAthletes[0]?._id ?? '');
  const [sessions, setSessions] = useState<ISession[]>([]);
  const [loading, setLoading] = useState(false);

  // Reset selected on roster change
  useEffect(() => {
    if (rosterAthletes.length > 0 && !rosterAthletes.find((a) => a._id === selectedAthleteId)) {
      setSelectedAthleteId(rosterAthletes[0]._id);
    }
  }, [rosterAthletes, selectedAthleteId]);

  useEffect(() => {
    if (!selectedAthleteId) return;
    setLoading(true);
    axios.get<ISession[]>(`${baseUrl}/sessions/athlete/${selectedAthleteId}?limit=200`)
      .then((res) => {
        const ps = dayjs(period.start); const pe = dayjs(period.end);
        setSessions(res.data.filter((s) => {
          const sd = dayjs(s.date);
          return sd.isAfter(ps.subtract(1, 'day')) && sd.isBefore(pe.add(1, 'day'));
        }));
      }).catch(() => setSessions([])).finally(() => setLoading(false));
  }, [selectedAthleteId, period]);

  const athlete = rosterAthletes.find((a) => a._id === selectedAthleteId);
  const role = primaryRole(athlete?.roles);
  const rc = roleColors(role);

  // Aggregati
  const totals = useMemo(() => {
    const sum = (g: (s: ISession) => number | undefined) =>
      sessions.reduce((acc, s) => acc + (Number(g(s)) || 0), 0);
    const td = sum((s) => s.commonMetrics?.td as number | undefined);
    const hsr = sum((s) => s.commonMetrics?.hsr as number | undefined);
    const sprint = sum((s) => s.commonMetrics?.sprintCount as number | undefined);
    const duration = sum((s) => s.duration);
    const srpe = sum((s) => s.internalLoad?.srpe as number | undefined);
    const rpeSum = sum((s) => s.internalLoad?.rpe as number | undefined);
    const vmaxPeak = sessions.reduce((m, s) => {
      const v = Number(s.commonMetrics?.maxVelocity) || 0; return v > m ? v : m;
    }, 0);
    return {
      sessions: sessions.length, duration, td, hsr, sprint, srpe,
      rpeAvg: sessions.length > 0 ? rpeSum / sessions.length : 0, vmaxPeak,
    };
  }, [sessions]);

  const rangeLabel = `${dayjs(period.start).format('DD MMM')} — ${dayjs(period.end).format('DD MMM YYYY')}`;

  const athleteOptions = rosterAthletes.map((a) => ({
    value: a._id,
    label: `${a.firstName} ${a.lastName}`,
  }));

  return (
    <Box className="leo-scope">
      {/* Header */}
      <Box px={4} py={3.5} bg={leoTokens.bg2} borderBottom="1px solid" borderColor={leoTokens.divider}
        display="flex" alignItems="center" gap={3.5} flexWrap="wrap">
        <Box w="38px" h="38px" borderRadius="50%" bg={rc.bg} color={rc.fg}
          display="flex" alignItems="center" justifyContent="center" fontSize="13px" fontWeight={700}>
          {athlete ? getInitials(athlete.firstName, athlete.lastName) : '—'}
        </Box>
        <div style={{ minWidth: 220 }}>
          <CustomSelectInput
            options={athleteOptions}
            value={selectedAthleteId}
            onChange={(val: string) => setSelectedAthleteId(val)}
            placeholder="— seleziona atleta —"
            menuPosition="fixed"
          />
        </div>
        {role && (
          <Box as="span" fontSize="9.5px" fontWeight={700} color={rc.fg}
            px={1.5} py={0.5} borderRadius="4px" bg={rc.bg} letterSpacing="0.4px">{role}</Box>
        )}
        <Text ml="auto" fontSize="12px" color={leoTokens.muted}>
          Periodo <Box as="strong" color={leoTokens.ink} fontWeight={600}>{rangeLabel}</Box>
        </Text>
      </Box>

      {loading ? (
        <Box p={8} textAlign="center"><Spinner /></Box>
      ) : sessions.length === 0 ? (
        <Box p={8} textAlign="center" color={leoTokens.muted}>
          Nessuna sessione per {athlete?.firstName} {athlete?.lastName} nel periodo
        </Box>
      ) : (
        <>
          {/* KPI cards */}
          <Box px={4} py={4} bg={leoTokens.bg2} borderBottom="1px solid" borderColor={leoTokens.divider}
            display="grid" gap={2.5}
            style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))' }}>
            <KpiCard label="Sessioni" value={String(totals.sessions)} unit="" accent="neutral" />
            <KpiCard label="Tempo totale" value={String(totals.duration)} unit="min" accent="neutral" />
            <KpiCard label="Distanza tot" value={(totals.td / 1000).toFixed(1)} unit="km" accent="success" />
            <KpiCard label="HSR totale" value={(totals.hsr / 1000).toFixed(1)} unit="km" accent="primary" />
            <KpiCard label="RPE medio" value={totals.rpeAvg.toFixed(1)} unit="/10" accent="warning" />
            <KpiCard label="sRPE totale" value={String(Math.round(totals.srpe))} unit="AU" accent="warning" />
            <KpiCard label="Picco V max" value={totals.vmaxPeak.toFixed(1)} unit="km/h" accent="error" />
          </Box>

          {/* Sessions table */}
          <Box overflowX="auto" bg="var(--c-card-bg)">
            <table>
              <thead>
                <tr>
                  <Th left>Data</Th><Th left>Sessione</Th>
                  <Th>Durata <Hint>min</Hint></Th>
                  <Th>TD <Hint>m</Hint></Th>
                  <Th>HSR <Hint>m</Hint></Th>
                  <Th>Sprint <Hint>n</Hint></Th>
                  <Th>V max <Hint>km/h</Hint></Th>
                  <Th>RPE <Hint>0-10</Hint></Th>
                  <Th>sRPE <Hint>AU</Hint></Th>
                </tr>
              </thead>
              <tbody>
                {[...sessions].sort((a, b) => dayjs(b.date).diff(dayjs(a.date))).map((s, idx) => {
                  const isMatch = s.sessionType === 'match' || s.sessionType === 'friendly';
                  const bg = isMatch ? '#fff9e8' : idx % 2 === 1 ? leoTokens.rowStripe : 'white';
                  return (
                    <tr key={String(s._id)} style={{ background: bg }}>
                      <td className="leo-mono" style={{ ...tdBase, paddingLeft: 28, fontSize: 12, fontWeight: 500 }}>
                        {dayjs(s.date).format('DD/MM')}
                      </td>
                      <td style={tdBase}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          {isMatch && <span style={{ width: 6, height: 6, borderRadius: '50%', background: leoTokens.brandStrong }} />}
                          <div>
                            <div style={{ fontSize: 13, fontWeight: 600, color: leoTokens.ink, lineHeight: 1.2 }}>
                              {s.name ?? s.sessionType}
                            </div>
                            <div style={{ fontSize: 11, color: leoTokens.muted, marginTop: 1 }}>{s.sport ?? s.sessionType}</div>
                          </div>
                        </div>
                      </td>
                      <Td v={s.duration} />
                      <Td v={s.commonMetrics?.td as number | undefined} />
                      <Td v={s.commonMetrics?.hsr as number | undefined} />
                      <Td v={s.commonMetrics?.sprintCount as number | undefined} />
                      <Td v={s.commonMetrics?.maxVelocity as number | undefined} dec={1} />
                      <Td v={s.internalLoad?.rpe as number | undefined} dec={1} />
                      <Td v={s.internalLoad?.srpe as number | undefined} />
                    </tr>
                  );
                })}
                <tr style={{ background: leoTokens.summaryBgEmphasis, borderTop: `1px solid ${leoTokens.secondary0}` }}>
                  <td style={{ ...tdBase, paddingLeft: 28, fontWeight: 700, color: leoTokens.ink }}>Σ Totali</td>
                  <td style={tdBase} />
                  <Td v={totals.duration} bold />
                  <Td v={totals.td} bold />
                  <Td v={totals.hsr} bold />
                  <Td v={totals.sprint} bold />
                  <Td v={totals.vmaxPeak} dec={1} bold />
                  <Td v={totals.rpeAvg} dec={1} bold />
                  <Td v={Math.round(totals.srpe)} bold />
                </tr>
              </tbody>
            </table>
          </Box>
        </>
      )}
    </Box>
  );
};

/* ──────── Helpers ──────── */

const tdBase: React.CSSProperties = {
  padding: '11px 12px',
  borderBottom: `1px solid ${leoTokens.rowDivider}`,
  whiteSpace: 'nowrap',
  textAlign: 'center',
};

const Th: React.FC<React.PropsWithChildren<{ left?: boolean }>> = ({ children, left }) => (
  <th style={{
    position: 'sticky', top: 0, zIndex: 3, background: leoTokens.headerBg,
    padding: '10px 12px', paddingLeft: left ? 28 : 12,
    borderBottom: `1px solid ${leoTokens.dividerStrong}`,
    textAlign: left ? 'left' : 'center',
    fontSize: 11, fontWeight: 700, color: leoTokens.ink,
    textTransform: 'uppercase', letterSpacing: 0.4,
  }}>{children}</th>
);

const Hint: React.FC<React.PropsWithChildren> = ({ children }) => (
  <span className="leo-mono" style={{ fontSize: 9.5, color: leoTokens.muted, fontWeight: 400, marginLeft: 4, textTransform: 'lowercase', letterSpacing: 0 }}>{children}</span>
);

const Td: React.FC<{ v: number | undefined; dec?: number; bold?: boolean }> = ({ v, dec = 0, bold }) => {
  const txt = v == null || Number.isNaN(v) ? '—'
    : dec > 0 ? Number(v).toFixed(dec)
    : Math.round(Number(v)).toLocaleString('it-IT');
  return (
    <td className="leo-mono" style={{ ...tdBase, fontSize: 12.5, fontWeight: bold ? 700 : 500, color: leoTokens.ink }}>
      {txt}
    </td>
  );
};

const KpiCard: React.FC<{ label: string; value: string; unit: string; accent: KpiAccent }> = ({ label, value, unit, accent }) => {
  const c = KPI_ACCENT[accent];
  return (
    <Box bg="var(--c-card-bg)" border="1px solid" borderColor={leoTokens.divider}
      borderTop={`2px solid ${c.bar}`} borderRadius="8px" px={3.5} py={2.5}>
      <Text fontSize="10px" fontWeight={700} letterSpacing="0.5px" textTransform="uppercase" color={leoTokens.muted}>
        {label}
      </Text>
      <Box display="flex" alignItems="baseline" gap={1} mt={1}>
        <Text className="leo-mono" fontSize="22px" fontWeight={700} letterSpacing="-0.5px" color={c.fg} lineHeight={1}>
          {value}
        </Text>
        <Text fontSize="11px" color={leoTokens.muted}>{unit}</Text>
      </Box>
    </Box>
  );
};
